import { FC, useMemo, useState } from 'react';
import { Grid, Typography, Box, TextField, MenuItem, Chip, CircularProgress, Alert } from '@mui/material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianHomeworkHook } from '../../../../hooks/useGuardianAcademicHook';
import { useAcademicsFiltersContext } from '../../../../../academics/contexts/academicsFilters.context';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CheckCircle, Pending, Warning } from '@mui/icons-material';

const GuardianHomeworkView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const { academicYear, grade } = useAcademicsFiltersContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data, isLoading, error } = useGuardianHomeworkHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
      ...(subjectFilter !== 'all' && { subject: subjectFilter }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(academicYear && { academicYear }),
      ...(grade && { grade }),
    }
  );

  const homework = useMemo(() => data?.data || [], [data]);
  const totalCount = data?.count ?? 0;

  const subjects = useMemo(() => {
    const uniqueSubjects = Array.from(new Set(homework.map((h) => h.subject)));
    return uniqueSubjects.sort();
  }, [homework]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'pending':
        return <Pending sx={{ color: 'warning.main' }} />;
      case 'overdue':
        return <Warning sx={{ color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'subject',
      headerName: translateText('guardian.academics.homework.columns.subject'),
      width: 150,
      flex: 1,
    },
    {
      field: 'title',
      headerName: translateText('guardian.academics.homework.columns.title'),
      width: 200,
      flex: 1,
    },
    {
      field: 'description',
      headerName: translateText('guardian.academics.homework.columns.description'),
      width: 250,
      flex: 1,
    },
    {
      field: 'dueDate',
      headerName: translateText('guardian.academics.homework.columns.dueDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(params.value);
        const today = new Date();
        const isOverdue = date < today && params.row.status !== 'completed';
        return (
          <Typography
            variant="body2"
            color={isOverdue ? 'error' : 'text.primary'}
            fontWeight={isOverdue ? 600 : 400}
          >
            {date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: translateText('guardian.academics.homework.columns.status'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as string;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {getStatusIcon(status)}
            <Chip
              label={translateText(`guardian.academics.homework.status.${status}`)}
              size="small"
              color={getStatusColor(status)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
  ], [translateText]);

  const rows = useMemo(() => {
    return homework.map((hw) => ({
      id: hw.id,
      subject: hw.subject,
      title: hw.title,
      description: hw.description,
      dueDate: hw.dueDate,
      status: hw.status,
    }));
  }, [homework]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-homework-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-homework-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-homework-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-homework-container">
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="guardian-homework-filters"
        >
          <TextField
            select
            label={translateText('guardian.academics.homework.filters.subject')}
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-homework-subject-filter"
          >
            <MenuItem value="all">{translateText('common.all')}</MenuItem>
            {subjects.map((subject) => (
              <MenuItem key={subject} value={subject}>
                {subject}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={translateText('guardian.academics.homework.filters.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-homework-status-filter"
          >
            <MenuItem value="all">{translateText('common.all')}</MenuItem>
            <MenuItem value="completed">{translateText('guardian.academics.homework.status.completed')}</MenuItem>
            <MenuItem value="pending">{translateText('guardian.academics.homework.status.pending')}</MenuItem>
            <MenuItem value="overdue">{translateText('guardian.academics.homework.status.overdue')}</MenuItem>
          </TextField>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <PaginatedGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          dataTestId="guardian-homework-grid"
          serverSide={true}
          totalCount={totalCount}
          currentPage={page}
          currentPageSize={pageSize}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Grid>
    </Grid>
  );
};

export default GuardianHomeworkView;
